import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { SubmitButton } from "@/components/form/SubmitButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/features/auth/AuthContext";
import { formatApiError } from "@/features/auth/formatError";
import type { PortalKey } from "@/features/auth/roles";

type Props = {
    portal: PortalKey;
    title: string;
    subtitle: string;
    homePath: string;
    accent?: string; // tailwind bg-* class for the badge
    icon: React.ReactNode;
    testId: string;
};

/**
 * Shared portal login form. Each role has its own dedicated route + page,
 * but the actual form is identical minus branding — this component keeps
 * things DRY without hiding intent behind a generic auth screen.
 */
export function PortalLoginForm({
    portal,
    title,
    subtitle,
    homePath,
    accent = "bg-ink",
    icon,
    testId,
}: Props) {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSubmitting(true);
        try {
            await login({
                email: email.trim(),
                password,
                remember: true,
                portal,
            });
            const redirect =
                (location.state as { from?: string } | null)?.from ?? homePath;
            navigate(redirect, { replace: true });
            toast.success(`Welcome to ${title}`);
        } catch (err) {
            setError(formatApiError(err, "Sign-in failed"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid={testId} className="space-y-6">
            <div className="flex items-center gap-3">
                <span
                    className={`grid h-11 w-11 place-items-center rounded-2xl text-white ${accent}`}
                >
                    {icon}
                </span>
                <div>
                    <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">
                        {title}
                    </h1>
                    <p className="text-sm text-muted-foreground">{subtitle}</p>
                </div>
            </div>

            <form className="space-y-4" onSubmit={onSubmit} aria-label={`${title} login form`}>
                <div className="space-y-1.5">
                    <Label htmlFor={`${testId}-email`}>Email</Label>
                    <div className="relative">
                        <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            id={`${testId}-email`}
                            type="email"
                            className="pl-9"
                            data-testid={`${testId}-email`}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                            required
                        />
                    </div>
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor={`${testId}-pw`}>Password</Label>
                    <div className="relative">
                        <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            id={`${testId}-pw`}
                            type="password"
                            className="pl-9"
                            data-testid={`${testId}-password`}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                            required
                        />
                    </div>
                </div>

                {error ? (
                    <p
                        role="alert"
                        data-testid={`${testId}-error`}
                        className="text-sm font-medium text-error"
                    >
                        {error}
                    </p>
                ) : null}

                <SubmitButton
                    type="submit"
                    loading={submitting}
                    data-testid={`${testId}-submit`}
                    className={`${accent} text-white hover:opacity-90`}
                >
                    Sign in
                </SubmitButton>

                <div className="flex items-center justify-end text-xs">
                    <a
                        href="/forgot-password"
                        data-testid={`${testId}-forgot`}
                        className="font-semibold text-muted-foreground hover:text-foreground"
                    >
                        Forgot your password?
                    </a>
                </div>
            </form>

            <p className="text-center text-xs text-muted-foreground">
                Wrong portal? Every role has its own login — ask your admin for the right link.
            </p>
        </div>
    );
}
